import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useMemo } from 'react';
import { Stack, Typography, Box, CircularProgress } from '@mui/material';
import { useQuery } from 'react-query';
import { PieChartWrapper, BarChartWrapper } from '../../charts';

import { ReadOnlyDataGrid, ReadOnlyField } from '../../ReadOnlyDataGrid';
import {
  DiagnosisReadDTO,
  DiagnosisEnergyBalanceReadDTO,
  DiagnosisEnergyVectorsReadDTO,
} from '../../../generated/api-client';
import { DiagnosisStepHandle } from './Step.types';
import { useDiagnosisEnergyBalanceByDiagnosisId } from '../../../hooks/useDiagnosisEnergyBalance';
import { useDiagnosisEnergyVectorsByDiagnosisId } from '../../../hooks';
import { apiClient } from '../../../services/apiClient';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { formatNumberItalian } from '../../../utils/numberFormat';

interface DiagnosisEnergyBalanceStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

export const DiagnosisEnergyBalanceStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisEnergyBalanceStepProps>(
    ({ diagnosisId, diagnosis }, ref) => {
      const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
      const { data: energyBalanceData = [], isLoading: isLoadingBalance } =
        useDiagnosisEnergyBalanceByDiagnosisId(diagnosisId);

      const { data: energyVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(
        diagnosisId || ''
      );

      const { data: allConsumptions = [] } = useQuery(
        [`all-consumptions-for-balance-${diagnosisId}`],
        async () => {
          if (!energyVectors.length) return [];
          const consumptions: any[] = [];
          for (const vector of energyVectors) {
            if (vector.id) {
              try {
                const vectorConsumptions =
                  await apiClient.get_by_energy_vector_id_diagnosis_consumption_by_energy_vector__energy_vector_id__get(
                    vector.id
                  );
                consumptions.push(...vectorConsumptions);
              } catch (error) {
                console.error(`Errore nel caricamento consumi per vettore ${vector.id}:`, error);
              }
            }
          }
          return consumptions;
        },
        { enabled: !!diagnosisId && energyVectors.length > 0 }
      );

      const totalConsumptions = useMemo(
        () => allConsumptions.filter(c => c.type === 'total'),
        [allConsumptions]
      );

      const energyVectorMap = useMemo(() => {
        const map = new Map<string, DiagnosisEnergyVectorsReadDTO>();
        energyVectors.forEach(vector => {
          if (vector.id) map.set(vector.id, vector);
        });
        return map;
      }, [energyVectors]);

      const tepTjTableData = useMemo(() => {
        return energyBalanceData
          .map(balance => {
            const vector = energyVectorMap.get(balance.diagnosis_energy_vector_id || '');
            const vectorTypeLabel = vector?.vector_type
              ? vectorTypeOptions.find(opt => opt.value === vector.vector_type)?.label ||
                vector.vector_type
              : '-';
            return {
              id: balance.id,
              vector: vectorTypeLabel,
              tep: balance.tep ? Number(balance.tep) : 0,
              tj: balance.tj ? Number(balance.tj) : 0,
            };
          })
          .filter(item => item.tep > 0 || item.tj > 0);
      }, [energyBalanceData, energyVectorMap]);

      const co2TableData = useMemo(() => {
        return energyBalanceData
          .map(balance => {
            const vector = energyVectorMap.get(balance.diagnosis_energy_vector_id || '');
            const vectorTypeLabel = vector?.vector_type
              ? vectorTypeOptions.find(opt => opt.value === vector.vector_type)?.label ||
                vector.vector_type
              : '-';
            return {
              id: balance.id,
              vector: vectorTypeLabel,
              value: (balance.co2 ? Number(balance.co2) : 0) / 1000,
            };
          })
          .filter(item => item.value > 0);
      }, [energyBalanceData, energyVectorMap]);

      // Ripartizione costi: dati solo da diagnosis_consumption.reference_period_price (e balance per TEP/TJ)
      const costTableData = useMemo(() => {
        return energyBalanceData.map(balance => {
          const vectorId = balance.diagnosis_energy_vector_id || '';
          const vector = energyVectorMap.get(vectorId);
          const vectorTypeLabel = vector?.vector_type
            ? vectorTypeOptions.find(opt => opt.value === vector.vector_type)?.label ||
              vector.vector_type
            : '-';
          const tep = balance.tep ? Number(balance.tep) : 0;
          const tj = balance.tj ? Number(balance.tj) : 0;

          const consumption = totalConsumptions.find(
            (c: any) => c.diagnosis_energy_vector_id === vectorId
          );
          const annualCost =
            consumption?.reference_period_price != null
              ? Number(consumption.reference_period_price)
              : 0;

          const unitPriceTep = tep > 0 ? annualCost / tep : 0;
          const unitPriceTj = tj > 0 ? annualCost / tj : 0;

          return {
            id: balance.id,
            vector: vectorTypeLabel,
            annualCost,
            unitPriceTep,
            unitPriceTj,
          };
        });
      }, [energyBalanceData, energyVectorMap, totalConsumptions, vectorTypeOptions]);

      const tepChartData = useMemo(() => {
        const total = tepTjTableData.reduce((sum, item) => sum + item.tep, 0);
        return tepTjTableData
          .filter(item => item.tep > 0)
          .map(item => ({
            id: item.id,
            label: item.vector,
            value: item.tep,
            percent: total > 0 ? (item.tep / total) * 100 : 0,
          }));
      }, [tepTjTableData]);

      const co2ChartData = useMemo(() => {
        const total = co2TableData.reduce((sum, item) => sum + item.value, 0);
        return co2TableData.map(item => ({
          id: item.id,
          label: item.vector,
          value: item.value,
          percent: total > 0 ? (item.value / total) * 100 : 0,
        }));
      }, [co2TableData]);

      const costBarChartData = useMemo(() => {
        return costTableData.map(item => ({
          id: item.id,
          label: item.vector,
          value: item.unitPriceTep,
        }));
      }, [costTableData]);

      const costPieChartData = useMemo(() => {
        const withCost = costTableData.filter(item => item.annualCost > 0);
        const total = withCost.reduce((sum, item) => sum + item.annualCost, 0);
        return withCost.map(item => ({
          id: item.id,
          label: item.vector,
          value: item.annualCost,
          percent: total > 0 ? (item.annualCost / total) * 100 : 0,
        }));
      }, [costTableData]);

      const CHART_HEIGHT = 220;

      React.useImperativeHandle(ref, () => ({
        save: async () => ({ success: true }),
      }));

      if (!diagnosisId) {
        return (
          <Typography color="text.secondary">
            Salva le informazioni principali della diagnosi per visualizzare il bilancio energetico.
          </Typography>
        );
      }

      const tepTjFields: ReadOnlyField[] = [
        { field: 'vector', headerName: 'Vettore energetico', width: 300 },
        {
          field: 'tep',
          headerName: 'tep',
          width: 200,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_tep,
        },
        {
          field: 'tj',
          headerName: 'TJ',
          width: 200,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_tj,
        },
      ];

      const costFields: ReadOnlyField[] = [
        { field: 'vector', headerName: 'Vettore energetico', width: 200 },
        {
          field: 'annualCost',
          headerName: 'Costo annuo [€]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_ripartizione_costi,
        },
        {
          field: 'unitPriceTep',
          headerName: 'Prezzo unitario [€/tep]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_ripartizione_costi,
        },
        {
          field: 'unitPriceTj',
          headerName: 'Prezzo unitario [€/TJ]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_ripartizione_costi,
        },
      ];

      const co2Fields: ReadOnlyField[] = [
        { field: 'vector', headerName: 'Vettore energetico', width: 300 },
        {
          field: 'value',
          headerName: 't',
          width: 200,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.bilancio_emissioni_co2,
        },
      ];

      const tepTotal = tepTjTableData.reduce((sum, item) => sum + item.tep, 0);
      const tjTotal = tepTjTableData.reduce((sum, item) => sum + item.tj, 0);
      const co2Total = co2TableData.reduce((sum, item) => sum + item.value, 0);
      const costTotal = costTableData.reduce((sum, item) => sum + item.annualCost, 0);
      const averageUnitCostTep = tepTotal > 0 ? costTotal / tepTotal : 0;
      const averageUnitCostTj = tjTotal > 0 ? costTotal / tjTotal : 0;

      return (
        <Box>
          <Typography variant="h6" sx={{ p: 2 }}>
            Bilancio energetico
          </Typography>

          {isLoadingBalance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                }}
              >
                <Box sx={{ flex: { lg: 1.4 }, p: 2, minWidth: 320 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Bilancio energetico aziendale
                  </Typography>
                  <ReadOnlyDataGrid
                    height="auto"
                    data={tepTjTableData}
                    fields={tepTjFields}
                    totalRow={{
                      vector: 'TOTALE',
                      tep: tepTotal,
                      tj: tjTotal,
                    }}
                    totalRowId="total"
                    getRowId={row => row.id}
                    showFooter={false}
                  />
                </Box>

                {tepChartData.length > 0 && (
                  <Box sx={{ flex: { lg: 1 }, p: 2, minWidth: 320 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Bilancio energetico aziendale [tep]
                    </Typography>
                    <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
                      <PieChartWrapper
                        series={[
                          {
                            data: tepChartData.map(item => ({
                              id: item.id,
                              value: item.value,
                              label: `${item.label} (${item.percent.toFixed(DECIMAL_PLACES.bilancio_percentuale)}%)`,
                            })),
                          },
                        ]}
                        width={600}
                        height={CHART_HEIGHT}
                        margin={{ right: 150 }}
                        legend={{
                          direction: 'column',
                          position: { vertical: 'middle', horizontal: 'right' },
                          padding: 24,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                }}
              >
                <Box sx={{ flex: { lg: 1.4 }, p: 2, minWidth: 320 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Ripartizione costi vettori energetici
                  </Typography>
                  <ReadOnlyDataGrid
                    height="auto"
                    data={costTableData}
                    fields={costFields}
                    totalRow={{
                      vector: 'TOTALE',
                      annualCost: costTotal,
                      unitPriceTep: averageUnitCostTep,
                      unitPriceTj: averageUnitCostTj,
                    }}
                    totalRowId="total"
                    getRowId={row => row.id}
                    showFooter={false}
                  />
                </Box>

                {costBarChartData.length > 0 && (
                  <Box sx={{ flex: { lg: 1 }, p: 2, minWidth: 320 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Costo unitario [€/tep] per vettore energetico
                    </Typography>
                    <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
                      <BarChartWrapper
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: costBarChartData.map(item => item.label),
                          },
                        ]}
                        series={[
                          {
                            data: costBarChartData.map(item => item.value),
                            label: 'Costo unitario [€/tep]',
                          },
                        ]}
                        barLabel={({ value }) =>
                          value != null
                            ? `${formatNumberItalian(Number(value), { minimumFractionDigits: 1, maximumFractionDigits: 2 })} €/tep`
                            : ''
                        }
                        slotProps={{
                          legend: { hidden: true },
                        }}
                        width={600}
                        height={250}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                }}
              >
                <Box sx={{ flex: { lg: 1.4 }, p: 2, minWidth: 320 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Emissioni equivalenti CO₂
                  </Typography>
                  <ReadOnlyDataGrid
                    height="auto"
                    data={co2TableData}
                    fields={co2Fields}
                    totalRow={{
                      vector: 'TOTALE',
                      value: co2Total,
                    }}
                    totalRowId="total"
                    getRowId={row => row.id}
                    showFooter={false}
                  />
                </Box>

                {co2ChartData.length > 0 && (
                  <Box sx={{ flex: { lg: 1 }, p: 2, minWidth: 320 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Emissioni equivalenti CO₂
                    </Typography>
                    <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
                      <PieChartWrapper
                        series={[
                          {
                            data: co2ChartData.map(item => ({
                              id: item.id,
                              value: item.value,
                              label: `${item.label} (${item.percent.toFixed(DECIMAL_PLACES.bilancio_percentuale)}%)`,
                            })),
                          },
                        ]}
                        width={600}
                        height={CHART_HEIGHT}
                        margin={{ right: 150 }}
                        legend={{
                          direction: 'column',
                          position: { vertical: 'middle', horizontal: 'right' },
                          padding: 24,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      );
    }
  )
);

DiagnosisEnergyBalanceStep.displayName = 'DiagnosisEnergyBalanceStep';
